import { useEffect, useCallback, useRef, useState } from 'react';
import { useConfetti } from './useConfetti';

// Konami code: ↑↑↓↓←→←→BA
const KONAMI_CODE = [
  'ArrowUp', 'ArrowUp',
  'ArrowDown', 'ArrowDown',
  'ArrowLeft', 'ArrowRight',
  'ArrowLeft', 'ArrowRight',
  'KeyB', 'KeyA',
] as const;

export interface UseEasterEggsOptions {
  onKonamiCode?: () => void;
  onLogoClick?: () => void;
  logoClickThreshold?: number;
  enabled?: boolean;
}

/**
 * Custom hook for Easter eggs in the application
 * Includes Konami code detection and logo click counter
 */
export function useEasterEggs(options: UseEasterEggsOptions = {}) {
  const {
    onKonamiCode,
    onLogoClick,
    logoClickThreshold = 10,
    enabled = true,
  } = options;

  const { fireEmojiConfetti, fireTournamentCompleteConfetti, fireSideCannons } = useConfetti();

  const [konamiActivated, setKonamiActivated] = useState(false);
  const [logoClickCount, setLogoClickCount] = useState(0);
  const [secretMode, setSecretMode] = useState(false);

  const konamiIndex = useRef(0);
  const logoClickTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Konami code listener
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const expectedKey = KONAMI_CODE[konamiIndex.current];

      if (event.code === expectedKey) {
        konamiIndex.current++;

        if (konamiIndex.current === KONAMI_CODE.length) {
          // Konami code completed!
          setKonamiActivated(true);
          konamiIndex.current = 0;

          // Trigger celebration
          fireTournamentCompleteConfetti();
          fireEmojiConfetti('🏓');

          // Call custom callback if provided
          if (onKonamiCode) {
            onKonamiCode();
          }

          // Reset after 5 seconds
          setTimeout(() => setKonamiActivated(false), 5000);
        }
      } else {
        // Reset if wrong key
        konamiIndex.current = 0;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, fireTournamentCompleteConfetti, fireEmojiConfetti, onKonamiCode]);

  // Logo click handler
  const handleLogoClick = useCallback(() => {
    if (!enabled) return;

    // Clear previous timeout
    if (logoClickTimeout.current) {
      clearTimeout(logoClickTimeout.current);
    }

    const newCount = logoClickCount + 1;
    setLogoClickCount(newCount);

    // Check if threshold reached
    if (newCount >= logoClickThreshold) {
      setSecretMode(true);
      setLogoClickCount(0);

      // Trigger celebration
      fireSideCannons();

      // Call custom callback if provided
      if (onLogoClick) {
        onLogoClick();
      }

      // Reset secret mode after 10 seconds
      setTimeout(() => setSecretMode(false), 10000);
    }

    // Reset click counter after 2 seconds of no clicks
    logoClickTimeout.current = setTimeout(() => {
      setLogoClickCount(0);
    }, 2000);
  }, [enabled, logoClickCount, logoClickThreshold, fireSideCannons, onLogoClick]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (logoClickTimeout.current) {
        clearTimeout(logoClickTimeout.current);
      }
    };
  }, []);

  return {
    konamiActivated,
    secretMode,
    logoClickCount,
    handleLogoClick,
    // Progress towards threshold (0-1)
    logoClickProgress: logoClickCount / logoClickThreshold,
  };
}

/**
 * Hook for tracking rapid clicks (double-click easter eggs)
 */
export function useRapidClicks(threshold: number = 5, timeWindow: number = 1000) {
  const [activated, setActivated] = useState(false);
  const clickTimes = useRef<number[]>([]);

  const handleClick = useCallback(() => {
    const now = Date.now();
    clickTimes.current.push(now);

    // Remove clicks outside time window
    clickTimes.current = clickTimes.current.filter(
      (time) => now - time < timeWindow
    );

    if (clickTimes.current.length >= threshold) {
      setActivated(true);
      clickTimes.current = [];
      setTimeout(() => setActivated(false), 3000);
    }
  }, [threshold, timeWindow]);

  return { activated, handleClick };
}

export interface UseShakeDetectionOptions {
  threshold?: number;
  timeout?: number;
  onShake?: () => void;
  enabled?: boolean;
}

/**
 * Hook for detecting shake gesture (mobile)
 */
export function useShakeDetection(options: UseShakeDetectionOptions = {}) {
  const {
    threshold = 15,
    timeout = 1000,
    onShake,
    enabled = true,
  } = options;

  const [shakeDetected, setShakeDetected] = useState(false);
  const lastUpdate = useRef(0);
  const lastX = useRef(0);
  const lastY = useRef(0);
  const lastZ = useRef(0);

  useEffect(() => {
    if (!enabled || !window.DeviceMotionEvent) return;

    const handleMotion = (event: DeviceMotionEvent) => {
      const { accelerationIncludingGravity } = event;
      if (!accelerationIncludingGravity) return;

      const { x, y, z } = accelerationIncludingGravity;
      if (x == null || y == null || z == null) return;
      const currentTime = Date.now();

      if (currentTime - lastUpdate.current > 100) {
        const diffTime = currentTime - lastUpdate.current;
        lastUpdate.current = currentTime;

        const speed = Math.abs(x + y + z - lastX.current - lastY.current - lastZ.current) / diffTime * 10000;

        if (speed > threshold) {
          setShakeDetected(true);
          onShake?.();
          setTimeout(() => setShakeDetected(false), timeout);
        }

        lastX.current = x;
        lastY.current = y;
        lastZ.current = z;
      }
    };

    window.addEventListener('devicemotion', handleMotion);
    return () => window.removeEventListener('devicemotion', handleMotion);
  }, [enabled, threshold, timeout, onShake]);

  return { shakeDetected };
}

export default useEasterEggs;
